import React, { useState, useEffect } from 'react';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { contactInfo as defaultContactInfo } from '../mock';

const ContactSection = () => {
  const [restaurantInfo, setRestaurantInfo] = useState(null);

  useEffect(() => {
    const fetchRestaurantInfo = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cms/restaurant-info`);
        if (response.ok) {
          const data = await response.json();
          if (data) {
            setRestaurantInfo(data);
          }
        }
      } catch (error) {
        console.error('Error fetching restaurant info:', error);
      }
    };
    fetchRestaurantInfo();
  }, []);

  // Use CMS data if available, otherwise fall back to mock
  const address = restaurantInfo?.address || defaultContactInfo.address;
  const phone = restaurantInfo?.phone || defaultContactInfo.phone;
  const email = defaultContactInfo.email; // Email stays from mock as it's not in CMS
  const openingHours = restaurantInfo?.opening_hours || null;

  return (
    <section id="contact" className="py-32 md:py-40 bg-[#faf9f7]">
      <div className="container mx-auto px-6 md:px-12 lg:px-20">
        
        {/* Section Header */}
        <div className="text-center mb-20 md:mb-24">
          <p 
            className="text-sm uppercase tracking-[0.3em] text-gray-500 mb-6"
            style={{ fontFamily: "var(--font-body)" }}
          >
            Informations pratiques
          </p>
          <h2 
            className="text-4xl md:text-5xl lg:text-6xl font-light mb-8 text-gray-900 tracking-[0.15em] uppercase"
            style={{ fontFamily: "var(--font-display)" }}
          >
            Nous Trouver
          </h2>
          <div className="w-16 h-px bg-gray-400 mx-auto"></div>
        </div>

        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 lg:gap-20">
            
            {/* Contact Info */}
            <div className="space-y-10">
              {/* Address */}
              <div className="flex items-start space-x-5">
                <div className="bg-gray-900 text-white p-3 rounded-lg flex-shrink-0">
                  <MapPin size={22} />
                </div>
                <div>
                  <h3 
                    className="text-lg mb-2 text-gray-900 tracking-wide uppercase"
                    style={{ fontFamily: "var(--font-display)" }}
                  >
                    Adresse
                  </h3>
                  <p 
                    className="text-lg text-gray-600"
                    style={{ fontFamily: "var(--font-body)", lineHeight: '1.8' }}
                  >
                    {address}
                  </p>
                </div>
              </div>

              {/* Phone */}
              <div className="flex items-start space-x-5">
                <div className="bg-gray-900 text-white p-3 rounded-lg flex-shrink-0">
                  <Phone size={22} />
                </div>
                <div>
                  <h3 
                    className="text-lg mb-2 text-gray-900 tracking-wide uppercase"
                    style={{ fontFamily: "var(--font-display)" }}
                  >
                    Téléphone
                  </h3>
                  <a
                    href={`tel:${phone}`}
                    className="text-lg text-gray-600 hover:text-gray-900 transition-colors"
                    style={{ fontFamily: "var(--font-body)" }}
                  >
                    {phone}
                  </a>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-start space-x-5">
                <div className="bg-gray-900 text-white p-3 rounded-lg flex-shrink-0">
                  <Mail size={22} />
                </div>
                <div>
                  <h3 
                    className="text-lg mb-2 text-gray-900 tracking-wide uppercase"
                    style={{ fontFamily: "var(--font-display)" }}
                  >
                    Email
                  </h3>
                  <a
                    href={`mailto:${email}`}
                    className="text-lg text-gray-600 hover:text-gray-900 transition-colors"
                    style={{ fontFamily: "var(--font-body)" }}
                  >
                    {email}
                  </a>
                </div>
              </div>

              {/* Hours */}
              <div className="flex items-start space-x-5">
                <div className="bg-gray-900 text-white p-3 rounded-lg flex-shrink-0">
                  <Clock size={22} />
                </div>
                <div>
                  <h3 
                    className="text-lg mb-4 text-gray-900 tracking-wide uppercase"
                    style={{ fontFamily: "var(--font-display)" }}
                  >
                    Horaires d'ouverture
                  </h3>
                  <div 
                    className="space-y-2"
                    style={{ fontFamily: "var(--font-body)" }}
                  >
                    {openingHours ? (
                      // Display CMS content with preserved line breaks
                      <div className="text-gray-600 text-base whitespace-pre-line leading-relaxed">
                        {openingHours}
                      </div>
                    ) : (
                      // Fallback if no CMS content
                      <p className="text-gray-500 italic text-sm">
                        Horaires non renseignés
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="overflow-hidden h-[500px]">
              <iframe
                title="La Rotonde Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2745.8234567890123!2d6.142857!3d46.356944!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDbCsDIxJzI1LjAiTiA2wrAwOCczNC4zIkU!5e0!3m2!1sfr!2sfr!4v1234567890123"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
